import { html, render, type TemplateResult } from 'lit-html';
import styles from './pk-key-value-grid.css?inline';

/**
 * `<pk-key-value-grid>` — definition-list pattern (label | value pairs).
 *
 * Consumers slot in pairs of `<dt>` (label) and `<dd>` (value) elements.
 * The component wraps the slotted pairs in a `<dl>` so screen readers parse
 * the relationship correctly. Layout is a two-column responsive grid that
 * collapses to stacked rows on narrow viewports.
 *
 * Slots:
 *   - default — a sequence of alternating `<dt>` and `<dd>` elements.
 *
 * Attributes:
 *   - `dense` — boolean; reduces vertical padding between rows.
 */
export class PkKeyValueGrid extends HTMLElement {
    static readonly tagName = 'pk-key-value-grid';

    static get observedAttributes(): string[] {
        return ['dense'];
    }

    private readonly _root: ShadowRoot;

    constructor() {
        super();
        this._root = this.attachShadow({ mode: 'open' });
    }

    connectedCallback(): void {
        this.render();
    }

    attributeChangedCallback(): void {
        this.render();
    }

    private template(): TemplateResult {
        const dense = this.hasAttribute('dense');
        return html`
            <style>${styles}</style>
            <dl class="kv-grid ${dense ? 'dense' : ''}">
                <slot></slot>
            </dl>
        `;
    }

    private render(): void {
        render(this.template(), this._root);
    }
}

if (!customElements.get(PkKeyValueGrid.tagName)) {
    customElements.define(PkKeyValueGrid.tagName, PkKeyValueGrid);
}

declare global {
    interface HTMLElementTagNameMap {
        'pk-key-value-grid': PkKeyValueGrid;
    }
}
